import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Search } from 'lucide-react';

const ProductosMasVendidos = ({ rotacionData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [productosFiltrados, setProductosFiltrados] = useState([]);
  const [modoVisualizacion, setModoVisualizacion] = useState('top'); // 'top' o 'search'
  
  // Colores para los gráficos
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];
  
  // Formatear número como moneda
  const formatCurrency = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '0.00';
    
    return new Intl.NumberFormat('es-PE', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  };
  
  // Preparar datos para visualización
  useEffect(() => {
    if (!rotacionData || rotacionData.length === 0) {
      setProductosFiltrados([]);
      return;
    }
    
    // Ordenar productos por cantidad vendida (de mayor a menor)
    const productosOrdenados = [...rotacionData]
      .map(item => ({
        ...item,
        cantidad: Number(item.cantidad) || 0,
        monto: Number(item.monto || item.total) || 0
      }))
      .sort((a, b) => b.cantidad - a.cantidad);
    
    if (modoVisualizacion === 'top') {
      // Para modo TOP: Mostrar los 4 más vendidos + "Otros"
      if (productosOrdenados.length <= 4) {
        setProductosFiltrados(productosOrdenados);
      } else {
        // Obtener los 4 primeros productos
        const top4 = productosOrdenados.slice(0, 4);
        
        // Calcular el total para "Otros"
        const otrosProductos = productosOrdenados.slice(4);
        const cantidadOtros = otrosProductos.reduce((sum, prod) => sum + prod.cantidad, 0);
        const montoOtros = otrosProductos.reduce((sum, prod) => sum + prod.monto, 0);
        
        // Crear el objeto "Otros"
        const otrosCategoria = {
          nombre: "Otros",
          cantidad: cantidadOtros,
          monto: montoOtros,
          isOtros: true
        };
        
        setProductosFiltrados([...top4, otrosCategoria]);
      }
    } else {
      // Para modo BÚSQUEDA / VER TODOS: Mostrar todos los productos vendidos o filtrar según término
      if (searchTerm.trim() === '') {
        setProductosFiltrados(productosOrdenados); // Mostrar todos los productos del período
      } else {
        const resultados = productosOrdenados.filter(
          prod => prod.nombre.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setProductosFiltrados(resultados);
      }
    }
  }, [rotacionData, searchTerm, modoVisualizacion]);
  
  // Manejar cambio en el campo de búsqueda
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim() !== '') {
      setModoVisualizacion('search');
    } else {
      setModoVisualizacion('top');
    }
  };
  
  // Manejar click en "Ver todos"
  const handleVerTodos = () => {
    setSearchTerm('');
    setModoVisualizacion('search');
  };
  
  // Manejar click en "Ver top"
  const handleVerTop = () => {
    setSearchTerm('');
    setModoVisualizacion('top');
  };
  
  // Si no hay datos
  if (!productosFiltrados || productosFiltrados.length === 0) {
    return (
      <div className="content-section">
        <h2 className="section-title">Productos más Vendidos</h2>
        <div className="no-data-container">
          <div className="no-data-header">
            <div>
              <p className="no-data-title">No hay productos vendidos</p>
              <p className="no-data-message">No hay datos de productos vendidos para mostrar en el período seleccionado.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section">
      <h2 className="section-title">Productos</h2>
      
      {/* Barra de búsqueda y filtros */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Buscar producto..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Search className="search-icon" />
      </div>
      
      <div className="filter-buttons">
        <button 
          className={`filter-button ${modoVisualizacion === 'top' ? 'filter-button-active' : 'filter-button-inactive'}`}
          onClick={handleVerTop}
        >
          Top 4 + Otros
        </button>
        <button 
          className={`filter-button ${modoVisualizacion === 'search' ? 'filter-button-active' : 'filter-button-inactive'}`}
          onClick={handleVerTodos}
        >
          Ver todos
        </button>
      </div>
      
      <div className="space-y-6">
        {modoVisualizacion === 'top' ? (
          <>
            {/* Lista de productos en tarjetas (Top 4 + Otros) */}
            <div>
              {productosFiltrados.map((producto, index) => (
                <div className="product-legend-item" key={`prod-${index}`}>
                  <div 
                    className="product-legend-color" 
                    style={{ backgroundColor: producto.isOtros ? COLORS[5] : COLORS[index % 5] }}
                  ></div>
                  <div className="product-legend-name">{producto.nombre}</div>
                  <div className="product-legend-metrics">
                    <span className="product-legend-quantity">{producto.cantidad} unidades</span>
                    <span className="product-legend-amount">S/ {formatCurrency(producto.monto)}</span>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Gráfico circular */}
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={productosFiltrados}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="cantidad"
                    nameKey="nombre"
                    label={false}
                  >
                    {productosFiltrados.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isOtros ? COLORS[5] : COLORS[index % 5]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => {
                      const cantidad = Number(value) || 0;
                      const total = Number(props.payload.monto) || 0;
                      return [
                        `${cantidad} unidades (S/ ${formatCurrency(total)})`,
                        props.payload.nombre
                      ];
                    }}
                  />
                  <Legend 
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          /* Modo VER TODOS / BÚSQUEDA: Tabla unificada limpia */
          productosFiltrados.length > 0 ? (
            <div>
              <table className="productos-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Cantidad</th>
                    <th>Ingresos</th>
                  </tr>
                </thead>
                <tbody>
                  {productosFiltrados.map((producto, index) => (
                    <tr key={`table-row-${index}`}>
                      <td>
                        <div className="flex items-center">
                          <span 
                            className="product-color-indicator" 
                            style={{ backgroundColor: COLORS[index % 5] }}
                          ></span>
                          {producto.nombre}
                        </div>
                      </td>
                      <td>{producto.cantidad} unidades</td>
                      <td>S/ {formatCurrency(producto.monto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <div className="no-results-icon">
                <Search size={32} />
              </div>
              <p className="no-results-message">No se encontraron productos</p>
              <p className="no-results-suggestion">Intenta con otro término de búsqueda</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ProductosMasVendidos;